import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { filterPrimaryDocuments } from '@repo/shared/lib';
import type { Document } from '@repo/shared/schema';
import {
  DocumentExtractionStatus,
  DocumentSide,
  DocumentType,
  ReportType,
} from '@repo/shared/types';
import { Badge } from '@repo/ui/badge';
import { Button } from '@repo/ui/button';
import { Card } from '@repo/ui/card';
import { cn } from '@repo/ui/lib/utils';
import {
  AlertCircle,
  ArrowRight,
  File,
  FileIcon,
  Loader2,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { Activity, useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams, useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';
import type { WizardLoaderData, WizardReport } from '@/lib/report';
import { deleteReportDocument, uploadReportDocument } from '@/lib/report-documents';
import { trpc } from '@/providers/trpc';
import { useSetUnsavedChanges } from '@/providers/unsaved-changes';
import { useOrganizationStore } from '@/stores/organization-store';
import { AppImage } from '../app-image';
import { ExtractionModal } from './extraction-modal';

// ── Document type config ──────────────────────────────────────────────────────

type DocTypeConfig = {
  value: string;
  label: string;
  sides: DocumentSide[];
};

const KYC_DOC_TYPES: DocTypeConfig[] = [
  {
    value: DocumentType.PASSPORT,
    get label() {
      return t('reports.passport');
    },
    sides: [DocumentSide.FRONT],
  },
  {
    value: DocumentType.NATIONAL_ID,
    get label() {
      return t('reports.nationalId');
    },
    sides: [DocumentSide.FRONT, DocumentSide.BACK],
  },
  {
    value: DocumentType.RESIDENCY_PERMIT,
    get label() {
      return t('reports.residencyPermit');
    },
    sides: [DocumentSide.FRONT],
  },
  {
    value: DocumentType.VOTER_ID,
    get label() {
      return t('reports.voterId');
    },
    sides: [DocumentSide.FRONT],
  },
];

const KYB_DOC_TYPES: DocTypeConfig[] = [
  {
    value: DocumentType.CERTIFICATE_OF_INCORPORATION,
    get label() {
      return t('reports.certificateOfIncorporation');
    },
    sides: [DocumentSide.FRONT],
  },
  {
    value: DocumentType.TRADE_LICENSE,
    get label() {
      return t('reports.tradeLicense');
    },
    sides: [DocumentSide.FRONT],
  },
  {
    value: DocumentType.COMMERCIAL_REGISTRY_EXTRACT,
    get label() {
      return t('reports.commercialRegistryExtract');
    },
    sides: [DocumentSide.FRONT],
  },
  {
    value: DocumentType.MERGER_AND_ACQUISITION,
    get label() {
      return t('reports.mergerAcquisitionDocument');
    },
    sides: [DocumentSide.FRONT],
  },
] as const;

// ── Component ─────────────────────────────────────────────────────────────────

type UploadStepProps = {
  onContinue: () => void;
};

export function UploadStep({ onContinue }: UploadStepProps) {
  useLocale();

  const { organizationId = '', reportId = '' } = useParams();
  const currentOrganization = useOrganizationStore((s) => s.currentOrganization);
  const orgId = currentOrganization?.id || organizationId;

  const { report: initialReport } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;

  const { setUnsavedChanges } = useSetUnsavedChanges();
  const hasMarkedUnsaved = useRef(false);

  const [extractionModalOpen, setExtractionModalOpen] = useState(false);

  // Live report — poll while extraction runs (modal open and/or job processing)
  const { data: report, refetch } = trpc.report.getReport.useQuery(
    { reportId },
    {
      initialData: initialReport as never,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        if (!data) return false;
        const primary = filterPrimaryDocuments(data.documents ?? []);
        const busy = primary.some(
          (d) =>
            d.extractionStatus === DocumentExtractionStatus.PENDING ||
            d.extractionStatus === DocumentExtractionStatus.PROCESSING,
        );
        const processing = primary.some(
          (d) => d.extractionStatus === DocumentExtractionStatus.PROCESSING,
        );
        const poll = busy && (processing || extractionModalOpen);
        return poll ? 2500 : false;
      },
    },
  );

  const primaryDocs = filterPrimaryDocuments(report?.documents ?? []);

  const docTypes: DocTypeConfig[] =
    initialReport.reportType === ReportType.KYC
      ? (KYC_DOC_TYPES as unknown as DocTypeConfig[])
      : (KYB_DOC_TYPES as unknown as DocTypeConfig[]);

  // The selected document type is inferred from already-uploaded docs or from local state
  const uploadedType = primaryDocs?.[0]?.documentType ?? null;
  const [selectedType, setSelectedType] = useState<string>(uploadedType ?? '');

  const activeTypeConfig = docTypes.find((t) => t.value === (uploadedType ?? selectedType));

  const [uploadingFor, setUploadingFor] = useState<Record<string, boolean>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── Upload ───────────────────────────────────────────────────────────────

  const handleDrop = useCallback(
    async (side: DocumentSide, files: File[]) => {
      const file = files[0];
      if (!file) return;

      const docType = uploadedType ?? selectedType;
      if (!docType) {
        toast.error(t('reports.selectADocumentTypeFirst'));
        return;
      }

      setUploadingFor((prev) => ({ ...prev, [side]: true }));
      try {
        await uploadReportDocument(orgId, reportId, file, docType, side);
        await refetch();
        if (!hasMarkedUnsaved.current) {
          hasMarkedUnsaved.current = true;
          setUnsavedChanges({
            get message() {
              return t('reports.youHaveStartedUploadingDocumentsForThisReportLeaving');
            },
            get proceedLinkLabel() {
              return t('extra.leaveAnyway');
            },
          });
        }
        toast.success(t('reports.fileUploaded'));
      } catch (err) {
        toast.error(t('reports.uploadFailed'), {
          description: errorMessage(AppError.parse(err)),
        });
      } finally {
        setUploadingFor((prev) => ({ ...prev, [side]: false }));
      }
    },
    [orgId, reportId, uploadedType, selectedType, refetch],
  );

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (docId: string) => {
    setDeletingId(docId);
    try {
      await deleteReportDocument(orgId, reportId, docId);
      await refetch();
      toast.success(t('reports.fileRemoved'));
    } catch (err) {
      toast.error(t('reports.failedToRemoveFile'), {
        description: errorMessage(AppError.parse(err)),
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Can continue? ─────────────────────────────────────────────────────────

  const requiredSides = activeTypeConfig?.sides ?? [DocumentSide.FRONT];
  const allRequiredUploaded =
    uploadedType === DocumentType.NATIONAL_ID
      ? [DocumentSide.FRONT, DocumentSide.BACK].every((s) => primaryDocs?.some((d) => d.side === s))
      : true;
  const hasAnyFailed = primaryDocs?.some(
    (d) => d.extractionStatus === DocumentExtractionStatus.FAILED,
  );

  const canContinue =
    primaryDocs?.length &&
    primaryDocs?.length > 0 &&
    uploadedType &&
    allRequiredUploaded &&
    !hasAnyFailed;

  const primaryExtractionAccepted =
    (primaryDocs?.length ?? 0) > 0 &&
    (primaryDocs ?? []).every(
      (d) =>
        d.isAcceptedByUser === true && d.extractionStatus === DocumentExtractionStatus.COMPLETED,
    );

  return (
    <div className="flex flex-col gap-6">
      {/* ── Document type selector ── */}
      {!uploadedType && (
        <div className="space-y-2">
          <p className="font-medium text-sm">{t('reports.selectDocumentType')}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {docTypes.map((dt) => (
              <button
                className={cn(
                  'rounded-lg border px-4 py-3 text-start font-medium text-sm transition-colors',
                  selectedType === dt.value
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/40 hover:bg-muted/50',
                )}
                key={dt.value}
                onClick={() => setSelectedType(dt.value)}
                type="button"
              >
                {dt.label}
                {dt.sides.length > 1 && (
                  <span className="ms-1.5 font-normal text-muted-foreground text-xs">
                    {t('reports.frontBack')}{' '}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Already-uploaded type badge + change option ── */}
      {uploadedType && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{activeTypeConfig?.label ?? uploadedType}</Badge>
          {primaryDocs?.length && primaryDocs?.length > 0 && (
            <button
              className="text-muted-foreground text-xs underline hover:text-foreground"
              onClick={async () => {
                for (const doc of primaryDocs) {
                  await handleDelete(doc._id);
                }
                setSelectedType('');
              }}
              type="button"
            >
              {t('reports.changeDocumentType')}{' '}
            </button>
          )}
        </div>
      )}
      <Activity
        mode={
          primaryDocs?.length &&
          primaryDocs?.length > 0 &&
          selectedType !== DocumentType.NATIONAL_ID
            ? 'visible'
            : 'hidden'
        }
      >
        {primaryDocs?.map((doc) => (
          <div className="flex items-center justify-start gap-2" key={doc._id}>
            {doc.mimeType?.startsWith('image/') ? (
              <AppImage
                alt={t('reports.documentPreview')}
                className="h-10 w-10 shrink-0 rounded border border-border object-cover"
                fileId={doc.fileId}
                type="thumbnail"
              />
            ) : (
              <FileIcon className="h-10 w-10 shrink-0 text-primary" />
            )}
            <span className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              {doc.fileName}
            </span>
          </div>
        ))}
      </Activity>
      {/* ── Dropzones ── */}
      {(uploadedType || selectedType) && (
        <div className="flex flex-col gap-4">
          {requiredSides.map((side) => {
            const existing = primaryDocs?.find((d) => d.side === side);
            const isUploading = uploadingFor[side];

            return (
              <DropzoneSlot
                existing={
                  existing
                    ? {
                        _id: existing._id,
                        documentType: existing.documentType,
                        extractionStatus: existing.extractionStatus,
                        fileId: existing.fileId,
                        fileName: existing.fileName,
                        mimeType: existing.mimeType,
                      }
                    : null
                }
                isDeleting={deletingId === existing?._id}
                isUploading={!!isUploading}
                key={side}
                onDelete={existing ? () => handleDelete(existing._id) : undefined}
                onDrop={(files) => handleDrop(side, files)}
                selectedType={selectedType}
                side={side}
              />
            );
          })}
        </div>
      )}

      {/* ── Failed extraction warning ── */}
      {hasAnyFailed && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-destructive text-sm">
            {t('reports.documentExtractionFailedPleaseDeleteAndReUploadThe')}{' '}
          </p>
        </div>
      )}

      {/* ── Continue button ── */}
      <div className="flex justify-end pt-2">
        <Button
          disabled={!canContinue}
          onClick={() => {
            if (primaryExtractionAccepted) {
              onContinue();
            } else {
              setExtractionModalOpen(true);
            }
          }}
        >
          {t('auth.continue')} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
        </Button>
      </div>

      {/* ── Extraction modal ── */}
      {report && !primaryExtractionAccepted && (
        <ExtractionModal
          onAccepted={() => {
            setExtractionModalOpen(false);
            void refetch();
            onContinue();
          }}
          onRejected={() => {
            setExtractionModalOpen(false);
            void refetch();
          }}
          open={extractionModalOpen}
          report={report as WizardReport}
        />
      )}
    </div>
  );
}

// ── DropzoneSlot sub-component ────────────────────────────────────────────────

type DropzoneSlotProps = {
  side: DocumentSide;
  selectedType: string;
  existing: Pick<
    Document,
    '_id' | 'documentType' | 'extractionStatus' | 'fileId' | 'fileName' | 'mimeType'
  > | null;
  isUploading: boolean;
  isDeleting: boolean;
  onDrop: (files: File[]) => void;
  onDelete?: () => void;
};

function DropzoneSlot({
  selectedType,
  side,
  existing,
  isUploading,
  isDeleting,
  onDrop,
  onDelete,
}: DropzoneSlotProps) {
  useLocale();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    multiple: false,
    disabled: !!existing || isUploading,
    onDrop,
    maxSize: 20 * 1024 * 1024, // 20 MB
  });

  const sideLabel = side === DocumentSide.FRONT ? t('extra.frontSide') : t('extra.backSide');

  return (
    <div className="space-y-1.5">
      {selectedType === DocumentType.NATIONAL_ID && (
        <p className="font-medium text-muted-foreground text-xs">{sideLabel}</p>
      )}

      {existing ? (
        // Uploaded state
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <DocumentThumbnail fileId={existing.fileId} mimeType={existing.mimeType ?? null} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-sm">
              {existing.fileName ?? t('reports.fileUploaded')}
            </p>
            <ExtractionStatusLine status={existing.extractionStatus} />
          </div>
          {onDelete && (
            <Button
              className="shrink-0"
              disabled={isDeleting}
              onClick={onDelete}
              size="icon"
              variant="ghost"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 text-destructive" />
              )}
            </Button>
          )}
        </div>
      ) : (
        // Dropzone state
        <Card
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed py-8 transition-colors',
            isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
            isUploading && 'pointer-events-none opacity-60',
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          ) : (
            <UploadCloud className="h-8 w-8 text-muted-foreground/50" />
          )}
          <div className="text-center">
            <p className="font-medium text-foreground text-sm">
              {isUploading
                ? t('reports.uploading')
                : isDragActive
                  ? t('reports.dropTheFileHere')
                  : t('reports.dropFileOrClickToBrowse')}
            </p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {t('reports.pdfJpgPngWebpMax20Mb')}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

function DocumentThumbnail({ fileId, mimeType }: { fileId: string; mimeType: string | null }) {
  useLocale();

  const isImage = mimeType?.startsWith('image/') ?? false;

  if (isImage) {
    return (
      <AppImage
        alt={t('reports.documentPreview')}
        className="h-10 w-10 shrink-0 rounded border border-border object-cover"
        fileId={fileId}
        type="thumbnail"
      />
    );
  }

  return <File className="h-5 w-5 shrink-0 text-primary" />;
}

function ExtractionStatusLine({ status }: { status: DocumentExtractionStatus }) {
  useLocale();

  const map: Record<DocumentExtractionStatus, { label: string; className: string }> = {
    [DocumentExtractionStatus.PENDING]: {
      get label() {
        return t('reports.waitingForExtraction');
      },
      className: 'text-muted-foreground',
    },
    [DocumentExtractionStatus.PROCESSING]: {
      get label() {
        return t('reports.extractingData');
      },
      className: 'text-yellow-600',
    },
    [DocumentExtractionStatus.COMPLETED]: {
      get label() {
        return t('reports.extractionComplete');
      },
      className: 'text-green-600',
    },
    [DocumentExtractionStatus.FAILED]: {
      get label() {
        return t('reports.extractionFailed');
      },
      className: 'text-destructive',
    },
  };
  const entry = map[status] ?? { label: status, className: 'text-muted-foreground' };
  return <p className={cn('text-xs', entry?.className)}>{entry?.label}</p>;
}
